import { useMemo, useState } from "react";
import { CARE_TYPES, DURATIONS, LOCATIONS, PROVIDERS } from "@/data/content";
import { WA_MESSAGES } from "@/config/site";
import WhatsAppCTA from "./WhatsAppCTA";
import ProviderCard from "./ProviderCard";

const selectCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-brand-charcoal focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/20 transition";

// Filters the actual directory data (PROVIDERS in src/data/content.js).
const CareMatcher = () => {
  const [location, setLocation] = useState("all");
  const [careType, setCareType] = useState("all");
  const [duration, setDuration] = useState("all");

  const results = useMemo(
    () =>
      PROVIDERS.filter(
        (p) =>
          (location === "all" || p.area === location) &&
          (careType === "all" || p.careTypes.includes(careType)) &&
          (duration === "all" || p.durations.includes(duration)),
      ),
    [location, careType, duration],
  );

  return (
    <div data-testid="care-matcher" className="rounded-3xl bg-white p-6 sm:p-10 shadow-[0_20px_60px_rgb(7,91,89,0.10)] border border-slate-100">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="matcher-location" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
            Location
          </label>
          <select id="matcher-location" data-testid="care-matcher-location-select" value={location} onChange={(e) => setLocation(e.target.value)} className={selectCls}>
            <option value="all">All of Penang</option>
            {LOCATIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="matcher-care-type" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
            Care Type
          </label>
          <select id="matcher-care-type" data-testid="care-matcher-care-type-select" value={careType} onChange={(e) => setCareType(e.target.value)} className={selectCls}>
            <option value="all">Any care type</option>
            {CARE_TYPES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="matcher-duration" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
            Preferred Duration
          </label>
          <select id="matcher-duration" data-testid="care-matcher-duration-select" value={duration} onChange={(e) => setDuration(e.target.value)} className={selectCls}>
            <option value="all">Any duration</option>
            {DURATIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div data-testid="care-matcher-results" className="mt-8">
        {results.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2">
            {results.map((p) => (
              <ProviderCard key={p.id} provider={p} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl bg-brand-tealLight p-6 sm:p-8 text-center">
            <p className="font-display font-semibold text-brand-teal text-lg">Our verified directory is growing</p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600 leading-relaxed">
              We are currently verifying facilities for this combination of location, care type and duration. Message us
              on WhatsApp with your requirements and we will help you explore suitable options — free of charge. We
              never claim a facility has vacancies unless availability has been confirmed.
            </p>
            <WhatsAppCTA testid="care-matcher-whatsapp-btn" message={WA_MESSAGES.homepage} label="Ask Us on WhatsApp" variant="gold" className="mt-5" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CareMatcher;
